import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { EduShell, EmptyState, StatusBadge, TEACHER_NAV } from "@/components/EduShell";
import { ClassAiSection } from "@/components/AiActivity";
import { Button } from "@/components/ui/button";
import {
  classJoinLink,
  downloadCsv,
  eduRpc,
  formatDue,
  qrCodeUrl,
} from "@/lib/education";

import { T } from "@/components/T";
import i18n from "@/i18n";
export const Route = createFileRoute("/teacher/classes/$classId")({
  component: TeacherClassDetail,
  head: () => ({
    meta: [
      { title: "Classroom - Inquira teacher" },
      { name: "description", content: "Manage a classroom roster, announcements, assignments and gradebook in Inquira." },
      { property: "og:title", content: "Inquira classroom" },
      { property: "og:description", content: "Roster, announcements, assignments and grades for one class." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

interface Stream {
  class: {
    id: string;
    name: string;
    subject: string | null;
    grade_level: string | null;
    description: string | null;
    join_code: string | null;
    join_code_enabled: boolean;
    class_period: string | null;
    term: string | null;
  } | null;
  announcements: Array<{ id: string; title: string | null; body: string; pinned: boolean; publish_at: string }>;
  assignments: Array<{ id: string; title: string; kind: string; due_at: string | null; status: string; points: number }>;
  resources: Array<{ id: string; title: string; url: string | null; body: string | null }>;
  events: Array<{ id: string; title: string; kind: string; starts_at: string }>;
}

interface RosterRow {
  student_id: string;
  display_name: string;
  joined_at: string;
  assignments_total: number;
  submitted_count: number;
  missing_count: number;
  average_score: number | null;
  last_activity: string | null;
}

interface Gradebook {
  assignments: Array<{ id: string; title: string; points: number }>;
  students: Array<{ student_id: string; student_name: string; cells: Record<string, { status: string; score: number | null }> }>;
}

const TABS = ["stream", "roster", "gradebook", "calendar", "AI"] as const;

function TeacherClassDetail() {
  const { classId } = useParams({ from: "/teacher/classes/$classId" });
  const [tab, setTab] = useState<(typeof TABS)[number]>("stream");
  const [stream, setStream] = useState<Stream | null>(null);
  const [roster, setRoster] = useState<RosterRow[]>([]);
  const [gradebook, setGradebook] = useState<Gradebook | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [eventForm, setEventForm] = useState({ title: "", starts_at: "", kind: "event" });

  const load = useCallback(async () => {
    const [s, r, g] = await Promise.all([
      eduRpc<Stream>("edu_class_stream", { _class_id: classId }),
      eduRpc<RosterRow[]>("edu_class_roster", { _class_id: classId }),
      eduRpc<Gradebook>("edu_gradebook", { _class_id: classId }),
    ]);
    if (s.error) toast.error(s.error);
    setStream(s.data);
    setRoster(r.data ?? []);
    setGradebook(g.data);
  }, [classId]);

  useEffect(() => { void load(); }, [load]);

  const cls = stream?.class;
  const code = cls?.join_code ?? "";

  async function post() {
    if (!announcement.trim()) return;
    const { error } = await eduRpc("edu_post_announcement", { _class_id: classId, _body: announcement });
    if (error) { toast.error(error); return; }
    setAnnouncement("");
    toast.success(i18n.t("edu.teacher_classes_classId.posted_to_the_class"));
    void load();
  }

  async function regenerate() {
    const { error } = await eduRpc<string>("edu_regenerate_join_code", { _class_id: classId });
    if (error) { toast.error(error); return; }
    toast.success(i18n.t("edu.teacher_classes_classId.new_class_code_created"));
    void load();
  }

  async function toggleCode() {
    const { error } = await eduRpc("edu_update_class", {
      _class_id: classId,
      _join_code_enabled: !(cls?.join_code_enabled ?? true),
    });
    if (error) { toast.error(error); return; }
    void load();
  }

  async function removeStudent(studentId: string) {
    const { error } = await eduRpc("edu_remove_student", { _class_id: classId, _student_id: studentId });
    if (error) { toast.error(error); return; }
    toast.success(i18n.t("edu.teacher_classes_classId.student_removed_from_class"));
    void load();
  }

  async function addEvent() {
    if (!eventForm.title || !eventForm.starts_at) { toast.error(i18n.t("edu.teacher_classes_classId.add_a_title_and_date")); return; }
    const { error } = await eduRpc("edu_create_event", {
      _class_id: classId,
      _title: eventForm.title,
      _starts_at: new Date(eventForm.starts_at).toISOString(),
      _kind: eventForm.kind,
    });
    if (error) { toast.error(error); return; }
    setEventForm({ title: "", starts_at: "", kind: "event" });
    void load();
  }

  function exportRoster() {
    downloadCsv(
      `class-performance-${classId}.csv`,
      roster.map((r) => ({
        student: r.display_name,
        joined: r.joined_at,
        assignments: r.assignments_total,
        submitted: r.submitted_count,
        missing: r.missing_count,
        average_score: r.average_score ?? "",
        last_activity: r.last_activity ?? "",
      })),
    );
  }

  return (
    <EduShell
      nav={TEACHER_NAV}
      title={cls?.name ?? "Classroom"}
      subtitle={[cls?.subject, cls?.grade_level, cls?.class_period, cls?.term].filter(Boolean).join(" · ")}
      actions={
        <Link
          to="/teacher/assignments/new"
          search={{ classId }}
          className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
        >
          New assignment
        </Link>
      }
    >
      <div className="mb-6 flex gap-1 overflow-x-auto text-sm">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-full px-3 py-1.5 font-semibold capitalize ${
              tab === t ? "border border-rule bg-card text-ink" : "text-ink-muted"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "stream" && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-rule bg-card p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted"><T k="edu.teacher_classes_classId.invite_students" /></p>
            <div className="mt-2 flex flex-wrap items-center gap-4">
              <span className="rounded-lg bg-background px-3 py-2 font-mono text-lg text-ink">{code || "—"}</span>
              {code && <img src={qrCodeUrl(classJoinLink(code))} alt={i18n.t("edu.teacher_classes_classId.class_join_qr_code")} className="size-24 rounded-lg bg-white p-1" />}
              <div className="flex flex-col gap-2 text-xs">
                {code && (
                  <button
                    className="text-left font-semibold text-neon-pink"
                    onClick={() => { void navigator.clipboard.writeText(classJoinLink(code)); toast.success(i18n.t("edu.teacher_classes_classId.invite_link_copied")); }}
                  >
                    Copy invite link
                  </button>
                )}
                <button className="text-left font-semibold text-ink-muted" onClick={regenerate}><T k="edu.teacher_classes_classId.regenerate_code" /></button>
                <button className="text-left font-semibold text-ink-muted" onClick={toggleCode}>
                  {cls?.join_code_enabled ? "Turn code off" : "Turn code on"}
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-rule bg-card p-4">
            <textarea
              className="w-full rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
              rows={3}
              placeholder={i18n.t("edu.teacher_classes_classId.post_an_announcement_to_the_class")}
              value={announcement}
              onChange={(e) => setAnnouncement(e.target.value)}
            />
            <Button className="mt-2" onClick={post}><T k="edu.teacher_classes_classId.post_announcement" /></Button>
          </div>

          {(stream?.announcements ?? []).map((a) => (
            <div key={a.id} className="rounded-2xl border border-rule bg-card p-4">
              {a.title && <p className="font-semibold text-ink">{a.title}</p>}
              <p className="whitespace-pre-wrap text-sm text-ink">{a.body}</p>
              <p className="mt-2 text-xs text-ink-muted">{new Date(a.publish_at).toLocaleString()}</p>
            </div>
          ))}

          <div>
            <h2 className="mb-3 font-display text-lg font-bold text-ink"><T k="edu.teacher_classes_classId.assignments" /></h2>
            {(stream?.assignments ?? []).length === 0 ? (
              <EmptyState title={i18n.t("edu.teacher_classes_classId.no_assignments_yet")} />
            ) : (
              <ul className="space-y-2">
                {stream!.assignments.map((a) => (
                  <li key={a.id} className="rounded-2xl border border-rule bg-card p-3">
                    <Link to="/teacher/assignments/$assignmentId" params={{ assignmentId: a.id }} className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-semibold text-ink">{a.title}</span>
                      <span className="flex items-center gap-2 text-xs text-ink-muted">
                        <StatusBadge status={a.status} />
                        {formatDue(a.due_at)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {tab === "roster" && (
        <div>
          <div className="mb-3 flex justify-end">
            <Button variant="outline" onClick={exportRoster}><T k="edu.teacher_classes_classId.export_csv" /></Button>
          </div>
          {roster.length === 0 ? (
            <EmptyState title={i18n.t("edu.teacher_classes_classId.no_students_yet")} body="Share the class code from the Stream tab." />
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-rule">
              <table className="w-full text-sm">
                <thead className="bg-card text-left text-xs uppercase text-ink-muted">
                  <tr>
                    <th className="p-3"><T k="edu.teacher_classes_classId.student" /></th>
                    <th className="p-3"><T k="edu.teacher_classes_classId.joined" /></th>
                    <th className="p-3"><T k="edu.teacher_classes_classId.completed" /></th>
                    <th className="p-3"><T k="edu.teacher_classes_classId.missing" /></th>
                    <th className="p-3"><T k="edu.teacher_classes_classId.average" /></th>
                    <th className="p-3"><T k="edu.teacher_classes_classId.last_activity" /></th>
                    <th className="p-3" />
                  </tr>
                </thead>
                <tbody>
                  {roster.map((r) => (
                    <tr key={r.student_id} className="border-t border-rule">
                      <td className="p-3 font-semibold text-ink">{r.display_name}</td>
                      <td className="p-3 text-ink-muted">{new Date(r.joined_at).toLocaleDateString()}</td>
                      <td className="p-3 text-ink-muted">{r.submitted_count}/{r.assignments_total}</td>
                      <td className="p-3 text-ink-muted">{r.missing_count}</td>
                      <td className="p-3 text-ink-muted">{r.average_score ?? "—"}</td>
                      <td className="p-3 text-ink-muted">{r.last_activity ? new Date(r.last_activity).toLocaleDateString() : "—"}</td>
                      <td className="p-3">
                        <button className="text-xs font-semibold text-destructive" onClick={() => removeStudent(r.student_id)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "gradebook" && (
        <div>
          <div className="mb-3 flex justify-end">
            <Button
              variant="outline"
              onClick={() =>
                downloadCsv(
                  `gradebook-${classId}.csv`,
                  (gradebook?.students ?? []).map((s) => {
                    const row: Record<string, unknown> = { student: s.student_name };
                    for (const a of gradebook?.assignments ?? []) {
                      row[a.title] = s.cells[a.id]?.score ?? s.cells[a.id]?.status ?? "missing";
                    }
                    return row;
                  }),
                )
              }
            >
              Export CSV
            </Button>
          </div>
          {(gradebook?.assignments ?? []).length === 0 ? (
            <EmptyState title={i18n.t("edu.teacher_classes_classId.nothing_to_grade_yet")} body="Publish an assignment to build the gradebook." />
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-rule">
              <table className="w-full text-sm">
                <thead className="bg-card text-left text-xs uppercase text-ink-muted">
                  <tr>
                    <th className="p-3"><T k="edu.teacher_classes_classId.student" /></th>
                    {gradebook!.assignments.map((a) => <th key={a.id} className="p-3">{a.title}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {gradebook!.students.map((s) => (
                    <tr key={s.student_id} className="border-t border-rule">
                      <td className="p-3 font-semibold text-ink">{s.student_name}</td>
                      {gradebook!.assignments.map((a) => {
                        const cell = s.cells[a.id];
                        return (
                          <td key={a.id} className="p-3 text-ink-muted">
                            {cell?.score ?? (cell ? cell.status.replace(/_/g, " ") : "missing")}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "calendar" && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-rule bg-card p-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <input
                className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
                placeholder={i18n.t("edu.teacher_classes_classId.event_title")}
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
              />
              <input
                type="datetime-local"
                className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
                value={eventForm.starts_at}
                onChange={(e) => setEventForm({ ...eventForm, starts_at: e.target.value })}
              />
              <select
                className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
                value={eventForm.kind}
                onChange={(e) => setEventForm({ ...eventForm, kind: e.target.value })}
              >
                <option value="event"><T k="edu.teacher_classes_classId.event" /></option>
                <option value="test"><T k="edu.teacher_classes_classId.test" /></option>
                <option value="quiz"><T k="edu.teacher_classes_classId.quiz" /></option>
              </select>
            </div>
            <Button className="mt-3" onClick={addEvent}><T k="edu.teacher_classes_classId.add_to_calendar" /></Button>
          </div>
          <ul className="space-y-2">
            {(stream?.events ?? []).map((e) => (
              <li key={e.id} className="flex items-center justify-between rounded-2xl border border-rule bg-card p-3">
                <span className="font-semibold text-ink">{e.title}</span>
                <span className="text-xs text-ink-muted">{e.kind} · {new Date(e.starts_at).toLocaleString()}</span>
              </li>
            ))}
            {(stream?.assignments ?? [])
              .filter((a) => a.due_at)
              .map((a) => (
                <li key={`due-${a.id}`} className="flex items-center justify-between rounded-2xl border border-dashed border-rule p-3">
                  <span className="text-ink">{a.title} due</span>
                  <span className="text-xs text-ink-muted">{formatDue(a.due_at)}</span>
                </li>
              ))}
          </ul>
        </div>
      )}

      {tab === "AI" && <ClassAiSection classId={classId} />}
    </EduShell>
  );
}
